/**
 * Dashboard page: sections (Overview, Powerbanks, Credit/Debit, Rents and Scans).
 * Filters (month, year, merchant, station) at top where applicable.
 * i18n: dashboard.* and language.* keys. Loading/error for counts API.
 */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import LinearProgress from '@mui/material/LinearProgress';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import PowerbanksStatusesDonut from '../mui-dashboard/components/PowerbanksStatusesDonut';
import PowerbanksConditionsDonut from '../mui-dashboard/components/PowerbanksConditionsDonut';
import LostPowerbanksLineChart from '../mui-dashboard/components/LostPowerbanksLineChart';
import LostPowerbanksSummaryDonut from '../mui-dashboard/components/LostPowerbanksSummaryDonut';
import DashboardBarChart from '../mui-dashboard/components/DashboardBarChart';
import DashboardMap from '../mui-dashboard/components/DashboardMap';
import SessionsChart from '../mui-dashboard/components/SessionsChart';
import UtilizationDonut from '../mui-dashboard/components/UtilizationDonut';
import StatCard from '../mui-dashboard/components/StatCard';
import * as api from '../../modules/api';
import {
  getPowerbanksDashboardInfo,
  getLostPowerbanksInfo,
  sortPowerbanksStatusesDashboardInfoByMerchant,
  sortPowerbanksConditionsDashboardInfoByMerchant,
  clearMerchantsPowerbanksStatusesDashboardSortList,
  clearMerchantsPowerbanksConditionsDashboardSortList,
} from '../../modules/dashboard/dashboardAction';

const EMPTY_BAR = { series: [], categories: [] };

function formatKpiValue(n, { money = false } = {}) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  const num = Number(n);
  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: money ? 2 : 0,
    minimumFractionDigits: money ? 0 : 0,
  }).format(num);
}

function formatTrendLabel(changePct) {
  if (changePct == null || Number.isNaN(Number(changePct))) return '0%';
  const n = Number(changePct);
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(1)}%`;
}

function metricToSeries(metric) {
  if (!metric || !Array.isArray(metric.series)) return [];
  return metric.series.map((v) => Number(v) || 0);
}

function metricCategories(metric) {
  if (!metric || !Array.isArray(metric.categories)) return [];
  return metric.categories.map(String);
}

function DashboardFilters({
  selectedMonth,
  selectedYear,
  selectedPartner,
  selectedMerchant,
  selectedStation,
  onMonthChange,
  onYearChange,
  onPartnerChange,
  onMerchantChange,
  onStationChange,
  partnersList,
  merchantsList,
  stationsList,
  showPartner,
  showMerchantStation,
  partnerHint,
}) {
  const { t } = useTranslation();
  const years = useMemo(() => {
    const y = new Date().getFullYear();
    return Array.from({ length: y - 2015 + 1 }, (_, i) => 2015 + i);
  }, []);

  const partnerOptions = Array.isArray(partnersList) ? partnersList : [];
  const merchantOptions = Array.isArray(merchantsList) ? merchantsList : [];

  const optionValue = (item) => {
    if (item == null) return '';
    if (typeof item === 'string') return item;
    return item.email || item.id || '';
  };
  const optionLabel = (item) => {
    if (item == null) return '';
    if (typeof item === 'string') return item;
    return item.email || item.name || item.id || '';
  };

  return (
    <Stack spacing={1} sx={{ mb: 2 }}>
      <Stack direction="row" flexWrap="wrap" gap={2}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel id="dashboard-month-label">{t('dashboard.month')}</InputLabel>
          <Select
            labelId="dashboard-month-label"
            label={t('dashboard.month')}
            value={selectedMonth}
            onChange={(e) => onMonthChange(Number(e.target.value))}
          >
            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((value) => (
              <MenuItem key={value} value={value}>{t(`dashboard.month${value}`)}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 90 }}>
          <InputLabel id="dashboard-year-label">{t('dashboard.year')}</InputLabel>
          <Select
            labelId="dashboard-year-label"
            label={t('dashboard.year')}
            value={selectedYear}
            onChange={(e) => onYearChange(Number(e.target.value))}
          >
            {years.map((y) => (
              <MenuItem key={y} value={y}>{y}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {showPartner && (
          <FormControl size="small" sx={{ minWidth: 200 }}>
            {/* shrink+notched: empty value ("All") otherwise overlaps the floating label */}
            <InputLabel id="dashboard-partner-label" shrink>{t('dashboard.partner')}</InputLabel>
            <Select
              labelId="dashboard-partner-label"
              label={t('dashboard.partner')}
              value={selectedPartner ?? ''}
              onChange={(e) => onPartnerChange(e.target.value || null)}
              displayEmpty
              notched
            >
              <MenuItem value="">{t('dashboard.all')}</MenuItem>
              {partnerOptions.map((p) => {
                const value = optionValue(p);
                if (!value) return null;
                return (
                  <MenuItem key={value} value={value}>{optionLabel(p)}</MenuItem>
                );
              })}
            </Select>
          </FormControl>
        )}
        {showMerchantStation && (
          <>
            <FormControl size="small" sx={{ minWidth: 200 }} disabled={showPartner && !selectedPartner}>
              <InputLabel id="dashboard-merchant-label" shrink>{t('dashboard.merchant')}</InputLabel>
              <Select
                labelId="dashboard-merchant-label"
                label={t('dashboard.merchant')}
                value={selectedMerchant ?? ''}
                onChange={(e) => onMerchantChange(e.target.value || null)}
                displayEmpty
                notched
              >
                <MenuItem value="">{t('dashboard.all')}</MenuItem>
                {merchantOptions.map((m) => {
                  const value = optionValue(m);
                  if (!value) return null;
                  return (
                    <MenuItem key={value} value={value}>{optionLabel(m)}</MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="dashboard-station-label" shrink>{t('dashboard.station')}</InputLabel>
              <Select
                labelId="dashboard-station-label"
                label={t('dashboard.station')}
                value={selectedStation ?? ''}
                onChange={(e) => onStationChange(e.target.value || null)}
                displayEmpty
                notched
              >
                <MenuItem value="">{t('dashboard.all')}</MenuItem>
                {Array.isArray(stationsList) && stationsList.map((s) => (
                  <MenuItem key={s} value={String(s)}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}
      </Stack>
      {showPartner && partnerHint ? (
        <Typography variant="caption" color="text.secondary">
          {partnerHint}
        </Typography>
      ) : null}
    </Stack>
  );
}

export default function DashboardPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const now = useMemo(() => new Date(), []);
  const [tab, setTab] = useState(0);
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth());
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [selectedStation, setSelectedStation] = useState(null);
  const [partnersList, setPartnersList] = useState([]);
  const [partnerMerchantsList, setPartnerMerchantsList] = useState([]);
  const [adminMerchantsList, setAdminMerchantsList] = useState([]);
  const [kpiPeriod, setKpiPeriod] = useState('month');
  const [overviewKpi, setOverviewKpi] = useState(null);
  const [countsLoading, setCountsLoading] = useState(true);
  const [countsError, setCountsError] = useState(null);
  const [snackOpen, setSnackOpen] = useState(false);

  const role = (typeof localStorage !== 'undefined' ? localStorage.getItem('role') : '') || '';
  const adminEmail = (typeof localStorage !== 'undefined' ? localStorage.getItem('Email') : '') || '';
  const isSuperAdmin = role === 'SUPER_ADMIN';
  const isAdmin = role === 'ADMIN';
  const isMerchant = role === 'MERCHANT';
  const showPartnerFilter = isSuperAdmin;
  const showMerchantStation = isSuperAdmin || isAdmin;
  /** Credit/Debit breakdown charts by role: SA — admins+merchants+stations; ADMIN — merchants+stations; MERCHANT — stations only. */
  const showCreditDebitByAdmins = isSuperAdmin;
  const showCreditDebitByMerchants = isSuperAdmin || isAdmin;
  const showCreditDebitByStations = isSuperAdmin || isAdmin || isMerchant;

  const {
    dataStatuses,
    dataLostPowerbanks,
    PowerbanksStatusesSortedByMerchant,
    PowerbanksConditionsSortedByMerchant,
  } = useSelector((state) => state.dashboardReducer || {});

  const fetchOverviewKpi = useCallback(async (period) => {
    setCountsLoading(true);
    setCountsError(null);
    try {
      const res = await api.getDashboardOverviewKpi(period);
      setOverviewKpi(res?.data || null);
    } catch (e) {
      setCountsError(e?.message || t('dashboard.error'));
      setSnackOpen(true);
      setOverviewKpi(null);
    } finally {
      setCountsLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchOverviewKpi(kpiPeriod); }, [fetchOverviewKpi, kpiPeriod]);
  useEffect(() => {
    dispatch(getPowerbanksDashboardInfo());
  }, [dispatch]);

  // SUPER_ADMIN: partners list for Partner filter.
  useEffect(() => {
    if (!isSuperAdmin) {
      setPartnersList([]);
      return;
    }
    let cancelled = false;
    api.getAdmins()
      .then((res) => {
        if (cancelled) return;
        const data = res?.data;
        const list = Array.isArray(data) ? data : (data?.merchants || data?.content || []);
        setPartnersList(list);
      })
      .catch(() => {
        if (!cancelled) setPartnersList([]);
      });
    return () => { cancelled = true; };
  }, [isSuperAdmin]);

  // SUPER_ADMIN: merchants under selected partner.
  useEffect(() => {
    if (!isSuperAdmin || !selectedPartner) {
      setPartnerMerchantsList([]);
      return;
    }
    let cancelled = false;
    api.getMerchantsBy(selectedPartner)
      .then((res) => {
        if (cancelled) return;
        const data = res?.data;
        const list = Array.isArray(data) ? data : (data?.merchants || data?.content || []);
        setPartnerMerchantsList(list);
      })
      .catch(() => {
        if (!cancelled) setPartnerMerchantsList([]);
      });
    return () => { cancelled = true; };
  }, [isSuperAdmin, selectedPartner]);

  // ADMIN: only merchants registered under this admin (same source as Merchants page).
  useEffect(() => {
    if (!isAdmin || !adminEmail) {
      setAdminMerchantsList([]);
      return;
    }
    let cancelled = false;
    api.getMerchantsBy(adminEmail)
      .then((res) => {
        if (cancelled) return;
        const data = res?.data;
        const list = Array.isArray(data) ? data : (data?.merchants || data?.content || []);
        setAdminMerchantsList(list);
      })
      .catch(() => {
        if (!cancelled) setAdminMerchantsList([]);
      });
    return () => { cancelled = true; };
  }, [isAdmin, adminEmail]);

  useEffect(() => {
    if (selectedMerchant || selectedPartner) {
      dispatch(sortPowerbanksStatusesDashboardInfoByMerchant(selectedMerchant || null, selectedPartner || null));
      dispatch(sortPowerbanksConditionsDashboardInfoByMerchant(selectedMerchant || null, selectedPartner || null));
    } else {
      dispatch(clearMerchantsPowerbanksStatusesDashboardSortList());
      dispatch(clearMerchantsPowerbanksConditionsDashboardSortList());
    }
  }, [dispatch, selectedMerchant, selectedPartner]);

  useEffect(() => {
    // Calendar month bounds as local YYYY-MM-DD (avoid timezone ISO shifts).
    const monthNum = selectedMonth + 1;
    const lastDay = new Date(selectedYear, monthNum, 0).getDate();
    const pad = (n) => String(n).padStart(2, '0');
    const start = `${selectedYear}-${pad(monthNum)}-01`;
    const end = `${selectedYear}-${pad(monthNum)}-${pad(lastDay)}`;
    dispatch(getLostPowerbanksInfo(
      start,
      end,
      selectedMerchant || null,
      selectedStation || null,
      selectedPartner || null
    ));
  }, [dispatch, selectedMonth, selectedYear, selectedMerchant, selectedStation, selectedPartner]);

  const kpiInterval = useMemo(() => {
    if (kpiPeriod === 'day') return t('dashboard.intervalDay');
    if (kpiPeriod === 'week') return t('dashboard.intervalWeek');
    return t('dashboard.intervalMonth');
  }, [kpiPeriod, t]);

  const dashboardKpiCards = useMemo(() => {
    const empty = { value: '—', trend: 'neutral', trendLabel: '0%', data: [], xAxisData: [] };
    const toCard = (title, metric, { money = false } = {}) => {
      if (!metric) {
        return { key: title, title, interval: kpiInterval, ...empty };
      }
      const trend = ['up', 'down', 'neutral'].includes(metric.trend) ? metric.trend : 'neutral';
      return {
        key: title,
        title,
        value: countsLoading ? '…' : formatKpiValue(metric.value, { money }),
        interval: kpiInterval,
        trend,
        trendLabel: formatTrendLabel(metric.changePct),
        data: metricToSeries(metric),
        xAxisData: metricCategories(metric),
      };
    };

    const cards = [
      toCard(t('dashboard.rents'), overviewKpi?.rents),
      toCard(t('dashboard.credit'), overviewKpi?.credit, { money: true }),
      toCard(t('dashboard.debit'), overviewKpi?.debit, { money: true }),
    ];
    if (isSuperAdmin && overviewKpi?.users != null) {
      cards.push(toCard(t('dashboard.users'), overviewKpi.users));
    } else if (isSuperAdmin && countsLoading) {
      cards.push(toCard(t('dashboard.users'), null));
    }
    return cards;
  }, [overviewKpi, countsLoading, kpiInterval, isSuperAdmin, t]);

  const statusesForCharts = useMemo(() => {
    if (PowerbanksStatusesSortedByMerchant?.allPowerBanks !== undefined) return PowerbanksStatusesSortedByMerchant;
    return dataStatuses;
  }, [dataStatuses, PowerbanksStatusesSortedByMerchant]);

  const conditionsForCharts = useMemo(() => {
    if (PowerbanksConditionsSortedByMerchant?.allPowerBanks !== undefined) return PowerbanksConditionsSortedByMerchant;
    return dataStatuses;
  }, [dataStatuses, PowerbanksConditionsSortedByMerchant]);

  const lostPowerBanks = dataLostPowerbanks?.lostPowerBanks ?? {};
  const utilization = dataLostPowerbanks?.utilization ?? EMPTY_BAR;
  const utilizationStations = dataLostPowerbanks?.utilizationStations ?? EMPTY_BAR;
  const creditDebit = dataLostPowerbanks?.creditDebit ?? EMPTY_BAR;
  const creditDebitAdmins = dataLostPowerbanks?.creditDebitAdmins ?? EMPTY_BAR;
  const creditDebitStations = dataLostPowerbanks?.creditDebitStations ?? EMPTY_BAR;
  const creditDebitMerchants = dataLostPowerbanks?.creditDebitMerchants ?? EMPTY_BAR;
  const rents = dataLostPowerbanks?.rents ?? EMPTY_BAR;
  const scans = dataLostPowerbanks?.scans ?? EMPTY_BAR;
  const rentScan = dataLostPowerbanks?.rentScan ?? EMPTY_BAR;

  const merchantsList = useMemo(() => {
    if (isSuperAdmin) return partnerMerchantsList;
    if (isAdmin) return adminMerchantsList;
    return [];
  }, [isSuperAdmin, isAdmin, partnerMerchantsList, adminMerchantsList]);

  // Prefer full station numbers from API (`stations`), not chart categories (only stations with activity).
  const stationsList = useMemo(() => {
    const fromApi = dataLostPowerbanks?.stations;
    if (Array.isArray(fromApi) && fromApi.length > 0) return fromApi;
    const util = utilizationStations?.categories;
    if (Array.isArray(util) && util.length > 0) return util;
    const cd = creditDebitStations?.categories;
    return Array.isArray(cd) ? cd : [];
  }, [dataLostPowerbanks?.stations, utilizationStations?.categories, creditDebitStations?.categories]);

  const creditDebitChartSeries = useMemo(() => {
    if (!creditDebit.series?.length) return null;
    const translateLabel = (name) => {
      const n = (name || '').toLowerCase();
      if (n === 'credit' || n === 'кредит') return t('dashboard.credit');
      if (n === 'debit' || n === 'дебет') return t('dashboard.debit');
      return name || 'Series';
    };
    return creditDebit.series.map((s) => ({
      id: (s.name || s.id || 'series').toLowerCase().replace(/\s+/g, '-'),
      label: translateLabel(s.name || s.id),
      data: Array.isArray(s.data) ? s.data : [],
    }));
  }, [creditDebit.series, t]);

  const creditDebitXAxisData = useMemo(() => {
    if (!creditDebit.categories?.length || selectedMonth == null || selectedYear == null) return [];
    const monthName = new Date(selectedYear, selectedMonth, 1).toLocaleDateString('en-US', { month: 'short' });
    return creditDebit.categories.map((d) => `${monthName} ${Number(d) || d}`);
  }, [creditDebit.categories, selectedMonth, selectedYear]);

  const translateCreditDebitSeriesName = useCallback((name) => {
    const n = (name || '').toLowerCase();
    if (n === 'credit' || n === 'кредит') return t('dashboard.credit');
    if (n === 'debit' || n === 'дебет') return t('dashboard.debit');
    return name || '';
  }, [t]);

  const creditDebitMerchantsTranslated = useMemo(() => {
    if (!creditDebitMerchants?.series?.length) return creditDebitMerchants;
    return {
      ...creditDebitMerchants,
      series: creditDebitMerchants.series.map((s) => ({
        ...s,
        name: translateCreditDebitSeriesName(s?.name),
      })),
    };
  }, [creditDebitMerchants, translateCreditDebitSeriesName]);

  const creditDebitAdminsTranslated = useMemo(() => {
    if (!creditDebitAdmins?.series?.length) return creditDebitAdmins;
    return {
      ...creditDebitAdmins,
      series: creditDebitAdmins.series.map((s) => ({
        ...s,
        name: translateCreditDebitSeriesName(s?.name),
      })),
    };
  }, [creditDebitAdmins, translateCreditDebitSeriesName]);

  const creditDebitStationsTranslated = useMemo(() => {
    if (!creditDebitStations?.series?.length) return creditDebitStations;
    return {
      ...creditDebitStations,
      series: creditDebitStations.series.map((s) => ({
        ...s,
        name: translateCreditDebitSeriesName(s?.name),
      })),
    };
  }, [creditDebitStations, translateCreditDebitSeriesName]);

  /** Графики «по админам / мерчантам / станциям»: по Y — категории (снизу вверх), по X — величина, в каждой строке две полосы — Кредит и Дебет */
  const creditDebitMerchantsForChart = useMemo(() => {
    const d = creditDebitMerchantsTranslated;
    if (!d?.categories?.length || !d?.series?.length) return d;
    return {
      categories: [...d.categories].reverse(),
      series: d.series.map((s) => ({ ...s, data: [...(s.data || [])].reverse() })),
    };
  }, [creditDebitMerchantsTranslated]);

  const creditDebitAdminsForChart = useMemo(() => {
    const d = creditDebitAdminsTranslated;
    if (!d?.categories?.length || !d?.series?.length) return d;
    return {
      categories: [...d.categories].reverse(),
      series: d.series.map((s) => ({ ...s, data: [...(s.data || [])].reverse() })),
    };
  }, [creditDebitAdminsTranslated]);

  const creditDebitStationsForChart = useMemo(() => {
    const d = creditDebitStationsTranslated;
    if (!d?.categories?.length || !d?.series?.length) return d;
    return {
      categories: [...d.categories].reverse(),
      series: d.series.map((s) => ({ ...s, data: [...(s.data || [])].reverse() })),
    };
  }, [creditDebitStationsTranslated]);

  const handlePartnerChange = useCallback((v) => {
    setSelectedPartner(v);
    setSelectedMerchant(null);
    setSelectedStation(null);
  }, []);

  const handleMerchantChange = useCallback((v) => {
    setSelectedMerchant(v);
    setSelectedStation(null);
  }, []);

  const filtersProps = {
    selectedMonth,
    selectedYear,
    selectedPartner,
    selectedMerchant,
    selectedStation,
    onMonthChange: setSelectedMonth,
    onYearChange: setSelectedYear,
    onPartnerChange: handlePartnerChange,
    onMerchantChange: handleMerchantChange,
    onStationChange: setSelectedStation,
    partnersList,
    merchantsList,
    stationsList,
    showPartner: showPartnerFilter,
    showMerchantStation,
    partnerHint: showPartnerFilter ? t('dashboard.partnerHint') : null,
  };
  return (
    <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' }, position: 'relative' }}>
      {countsLoading && (
        <LinearProgress sx={{ position: 'absolute', left: 0, right: 0, top: 0, zIndex: 1 }} />
      )}
      <Snackbar open={snackOpen} autoHideDuration={6000} onClose={() => setSnackOpen(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnackOpen(false)} severity="error">
          {countsError || t('dashboard.error')}
        </Alert>
      </Snackbar>
      <Typography component="h1" variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
        {t('dashboard.title')}
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tab label={t('dashboard.overview')} id="dashboard-tab-0" />
        <Tab label={t('dashboard.creditDebit')} id="dashboard-tab-1" />
        <Tab label={t('dashboard.rentsAndScans')} id="dashboard-tab-2" />
        <Tab label={t('dashboard.powerbanks')} id="dashboard-tab-3" />
      </Tabs>

      {/* Overview */}
      {tab === 0 && (
        <Box>
          <Typography component="h2" variant="subtitle1" color="text.secondary" sx={{ mb: 1.5 }}>
            {t('dashboard.stationsMap')}
          </Typography>
          <Grid container spacing={2} columns={12} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12 }}>
              <DashboardMap />
            </Grid>
          </Grid>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
            gap={1.5}
            sx={{ mb: 1.5 }}
          >
            <Typography component="h2" variant="subtitle1" color="text.secondary">
              {t('dashboard.overview')}
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={kpiPeriod}
              onChange={(_, v) => { if (v) setKpiPeriod(v); }}
              aria-label={t('dashboard.overview')}
            >
              <ToggleButton value="day">{t('dashboard.periodDay')}</ToggleButton>
              <ToggleButton value="week">{t('dashboard.periodWeek')}</ToggleButton>
              <ToggleButton value="month">{t('dashboard.periodMonth')}</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          <Grid container spacing={2} columns={12} sx={{ mb: 3 }}>
            {dashboardKpiCards.map((card) => (
              <Grid key={card.key} size={{ xs: 12, sm: 6, lg: 3 }}>
                <StatCard
                  title={card.title}
                  value={card.value}
                  interval={card.interval}
                  trend={card.trend}
                  trendLabel={card.trendLabel}
                  data={card.data}
                  xAxisData={card.xAxisData}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Powerbanks */}
      {tab === 3 && (
        <Box>
          <Typography component="h2" variant="subtitle1" color="text.secondary" sx={{ mb: 1.5 }}>
            {t('dashboard.filters')}
          </Typography>
          <DashboardFilters {...filtersProps} />
          <Grid container spacing={2} columns={12}>
            <Grid size={{ xs: 12, md: 6 }}>
              <PowerbanksStatusesDonut
                dataStatuses={statusesForCharts}
                title={t('dashboard.powerbanksStatuses')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <PowerbanksConditionsDonut
                dataStatuses={conditionsForCharts}
                title={t('dashboard.powerbanksConditions')}
              />
            </Grid>
            <Grid size={{ xs: 12, lg: 8 }}>
              <LostPowerbanksLineChart
                selectedMonth={selectedMonth}
                selectedYear={selectedYear}
                lostPowerBanks={lostPowerBanks}
                title={t('dashboard.lostPowerbanksByDay')}
              />
            </Grid>
            <Grid size={{ xs: 12, lg: 4 }}>
              <LostPowerbanksSummaryDonut
                dataStatuses={dataStatuses}
                lostPowerBanks={lostPowerBanks}
                title={t('dashboard.lostSummary')}
              />
            </Grid>
            {(utilization.categories?.length > 0 || utilizationStations.categories?.length > 0) && (
              <Grid size={{ xs: 12, md: 6 }}>
                <UtilizationDonut title={t('dashboard.stationUtilization')} utilization={utilization} utilizationStations={utilizationStations} />
              </Grid>
            )}
          </Grid>
        </Box>
      )}

      {/* Credit / Debit */}
      {tab === 1 && (
        <Box>
          <Typography component="h2" variant="subtitle1" color="text.secondary" sx={{ mb: 1.5 }}>
            {t('dashboard.filters')}
          </Typography>
          <DashboardFilters {...filtersProps} />
          <Grid container spacing={2} columns={12}>
            {creditDebit.categories?.length > 0 && creditDebitChartSeries?.length > 0 && (
              <Grid size={{ xs: 12, md: 6 }}>
                <SessionsChart
                  title={t('dashboard.revenueTotal')}
                  caption=""
                  xAxisData={creditDebitXAxisData}
                  series={creditDebitChartSeries}
                  hideSummary
                />
              </Grid>
            )}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined" sx={{ height: '100%', minHeight: 120 }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight={600}>
                    {t('dashboard.creditDebitLegendTitle')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {t('dashboard.revenueLegendIntro')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Chip label={t('dashboard.credit')} size="small" color="primary" variant="outlined" />
                      <Typography variant="body2" color="text.secondary">
                        — {t('dashboard.creditLegendDescription')}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Chip label={t('dashboard.debit')} size="small" color="secondary" variant="outlined" />
                      <Typography variant="body2" color="text.secondary">
                        — {t('dashboard.debitLegendDescription')}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            {showCreditDebitByAdmins && (
              <Grid size={{ xs: 12, md: 6 }}>
                {(creditDebitAdmins.categories?.length > 0) ? (
                  <DashboardBarChart
                    title={t('dashboard.creditDebitByAdmins')}
                    dataValues={creditDebitAdminsForChart}
                    height={Math.max(320, (creditDebitAdminsForChart.categories?.length || 0) * 28 + 80)}
                    stacked={false}
                    horizontal
                  />
                ) : (
                  <Card variant="outlined" sx={{ height: '100%', minHeight: 200 }}>
                    <CardContent>
                      <Typography component="h2" variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                        {t('dashboard.creditDebitByAdmins')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('dashboard.noChartData', { defaultValue: 'Нет данных за выбранный период' })}
                      </Typography>
                    </CardContent>
                  </Card>
                )}
              </Grid>
            )}
            {showCreditDebitByMerchants && (
              <Grid size={{ xs: 12, md: 6 }}>
                {(creditDebitMerchants.categories?.length > 0) ? (
                  <DashboardBarChart
                    title={t('dashboard.creditDebitByMerchants')}
                    dataValues={creditDebitMerchantsForChart}
                    height={Math.max(320, (creditDebitMerchantsForChart.categories?.length || 0) * 28 + 80)}
                    stacked={false}
                    horizontal
                  />
                ) : (
                  <Card variant="outlined" sx={{ height: '100%', minHeight: 200 }}>
                    <CardContent>
                      <Typography component="h2" variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                        {t('dashboard.creditDebitByMerchants')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('dashboard.noChartData', { defaultValue: 'Нет данных за выбранный период' })}
                      </Typography>
                    </CardContent>
                  </Card>
                )}
              </Grid>
            )}
            {showCreditDebitByStations && (
              <Grid size={{ xs: 12, md: 6 }}>
                {(creditDebitStations.categories?.length > 0) ? (
                  <DashboardBarChart
                    title={t('dashboard.creditDebitByStations')}
                    dataValues={creditDebitStationsForChart}
                    height={Math.max(320, (creditDebitStationsForChart.categories?.length || 0) * 28 + 80)}
                    stacked={false}
                    horizontal
                  />
                ) : (
                  <Card variant="outlined" sx={{ height: '100%', minHeight: 200 }}>
                    <CardContent>
                      <Typography component="h2" variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                        {t('dashboard.creditDebitByStations')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('dashboard.noChartData', { defaultValue: 'Нет данных за выбранный период' })}
                      </Typography>
                    </CardContent>
                  </Card>
                )}
              </Grid>
            )}
          </Grid>
        </Box>
      )}

      {/* Rents and Scans */}
      {tab === 2 && (
        <Box>
          <Typography component="h2" variant="subtitle1" color="text.secondary" sx={{ mb: 1.5 }}>
            {t('dashboard.filters')}
          </Typography>
          <DashboardFilters {...filtersProps} />
          <Grid container spacing={2} columns={12}>
            {rentScan.categories?.length > 0 && (
              <Grid size={{ xs: 12 }}>
                <DashboardBarChart
                  title={t('dashboard.rentScan')}
                  dataValues={rentScan}
                  height={Math.max(300, (rentScan.categories?.length || 0) * 28 + 80)}
                  stacked
                />
              </Grid>
            )}
            {rents.categories?.length > 0 && (
              <Grid size={{ xs: 12, md: 6 }}>
                <DashboardBarChart
                  title={t('dashboard.rents')}
                  dataValues={rents}
                  height={Math.max(300, (rents.categories?.length || 0) * 28 + 80)}
                  stacked
                />
              </Grid>
            )}
            {scans.categories?.length > 0 && (
              <Grid size={{ xs: 12, md: 6 }}>
                <DashboardBarChart
                  title={t('dashboard.scans')}
                  dataValues={scans}
                  height={Math.max(300, (scans.categories?.length || 0) * 28 + 80)}
                  stacked
                />
              </Grid>
            )}
          </Grid>
        </Box>
      )}
    </Box>
  );
}
